'use client';

import { getPageTitle } from '@/utilities/seo';
import { Metadata } from 'next';
import Link from 'next/link';
import Close from '../../../public/assets/icons/close.svg';
import Search from '../../../public/assets/icons/search.svg';
import { FormEvent, useState, useMemo } from 'react';
import classNames from 'classnames';
import { headerLinks } from '@/components/Nav/data';
import { ISubItem } from '@/components/Nav/utilities';

export const metadata: Metadata = {
  title: getPageTitle('Page not found'),
  description: 'This page could not be found.',
  robots: {
    index: false,
    follow: false,
    nocache: true,
    googleBot: {
      index: false,
      follow: false,
      noimageindex: false,
    },
  },
};

const SearchBar = () => {
  const [submittedValue, setSubmittedValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<ISubItem[]>([]);
  const [notFound, setNotFound] = useState(false);

  const clearSearch = () => {
    setSearchValue('');
    setNotFound(false);
    setSearchOptions([]);
    setSubmittedValue('');
  };

  const extendedFlatList = useMemo((): (ISubItem | undefined)[] =>  [
      ...headerLinks.flatMap(({ items }) => items).flatMap((e) => e?.items),
      {
        name: 'Contact',
        description:
          'Our dedicated support team is committed to providing you with the assistance and information you need to ensure a seamless and satisfying experience.',
        href: '/contact',
      },
      {
        name: 'Home',
        description:
          'Secure your workforce without compromising speed and usability.',
        href: '/',
      },
    ], []);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (searchValue.length < 2 || searchValue === submittedValue) {
      return;
    }

    setSubmittedValue(searchValue);
    setNotFound(false);

    const searchBy = searchValue.trim().toLowerCase();

    const category = headerLinks.find(({ name }) =>
      name.toLowerCase().includes(searchBy)
    );

    if (category?.items) {
      return setSearchOptions(category.items.flatMap(({ items }) => items));
    }

    const subCategory = headerLinks
      .flatMap(({ items }) => items)
      .find((e) => e?.name.toLowerCase().includes(searchBy));

    if (subCategory?.items) {
      return setSearchOptions(subCategory.items);
    }

    const filteredValues = extendedFlatList.filter(
      (item: undefined | ISubItem): item is ISubItem => {
        return !!(
          item?.name.toLowerCase().includes(searchBy) ||
          item?.description.toLowerCase().includes(searchBy)
        );
      }
    );

    setSearchOptions(filteredValues);
    setNotFound(!filteredValues.length);
  };

  return (
    <div className="group relative">
      <form className="relative" onSubmit={onSubmit}>
        <input
          type="text"
          placeholder="   Search all of Primary"
          className={classNames([
            'block w-full border-b-4 border-b-primary-default bg-background py-3 pl-3.5 pr-[4.5rem] outline-none transition-colors',
            notFound ? 'border-b-rose-400' : 'border-b-primary-default',
          ])}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
        <div className="absolute right-4 top-1/2 flex -translate-y-1/2 gap-2 text-[#7E869A] transition-all">
          <button
            className={classNames([
              'outline-none transition-all hover:text-accent focus:text-accent',
              searchValue?.length < 2 && 'invisible opacity-0',
            ])}
          >
            <Search />
          </button>
          <button
            className={classNames([
              'outline-none transition-all hover:text-accent focus:text-accent',
              !searchOptions.length &&
                searchValue?.length < 2 &&
                'invisible opacity-0',
            ])}
            type="button"
            onClick={clearSearch}
          >
            <Close />
          </button>
        </div>
      </form>
      {!!searchOptions.length && (
        <ul
          className={classNames([
            'absolute left-0 top-full mt-2 hidden max-h-96 w-full overflow-auto border border-background bg-white group-focus-within:grid',
          ])}
        >
          {searchOptions.map(({ name, href, description }, i) => (
            <li key={`${name}-${i}`}>
              <Link
                href={href}
                className="block w-full p-4 py-2 text-left text-sm outline-none transition-colors hover:bg-background focus:bg-background"
              >
                <span className="block font-semibold">{name}</span>
                <span className="block text-xs text-secondary">
                  {description}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
